import { Controller, useForm } from "react-hook-form";
import Button from "../../../../ui/Button/Button";
import Modal from "../../../../ui/Modal/Modal";
import TextInput from "../../../../ui/TextInput/TextInput";
import Label from "../../../../ui/Label/Label";
import { useCallback } from "react";
import { useMutation } from "urql";
import { z } from "zod";
import { graphql } from "../../../../__generated__/gql";
import styles from "./HeroPreviewModal.module.scss";
import { ImageProvider } from "../../../../__generated__/gql/graphql";
import { zodResolver } from "@hookform/resolvers/zod";

const UpdateImageMutation = graphql(/* GraphQL */ `
  mutation updateImage($input: UpdateImageInput!) {
    updateImage(input: $input) {
      id
      alt
    }
  }
`);

const schema = z.object({
  alt: z.string(),
});

type HeroPreviewModalFormData = z.infer<typeof schema>;

function InternalHeroPreviewModal({
  image,
}: {
  image: {
    id: string;
    src: string;
    alt?: string | null;
    height: number;
    width: number;
    provider: ImageProvider;
    author?: {
      name: string;
      href: string;
    };
  };
}) {
  const [, updateImage] = useMutation(UpdateImageMutation);

  const {
    control,
    handleSubmit,
    reset,
    formState: { isDirty, isSubmitting },
  } = useForm<HeroPreviewModalFormData>({
    defaultValues: {
      alt: image.alt ?? "",
    },
    resolver: zodResolver(schema),
  });

  const onSubmit = useCallback(
    async (data: HeroPreviewModalFormData) => {
      await updateImage({
        input: {
          id: image.id,
          alt: data.alt,
        },
      });
      reset(data);
    },
    [image.id, reset, updateImage],
  );

  return (
    <>
      <figure className={styles.heroImagePreview}>
        <div className={styles.heroImagePreviewImageContainer}>
          <img
            alt={image.alt ?? ""}
            className={styles.heroImagePreviewImage}
            src={image.src}
            height={image.height}
            width={image.width}
          />

          {image.author && (
            <footer className={styles.heroImagePreviewFooter}>
              by{" "}
              <a
                target="_blank"
                href={`${image.author.href}?utm_source=macaw&utm_medium=referral`}
                rel="noreferrer"
              >
                {image.author.name}
              </a>{" "}
              via{" "}
              {image.provider === ImageProvider.Pexels ? (
                <a
                  href="https://www.pexels.com"
                  target="_blank"
                  rel="noreferrer"
                >
                  Pexels
                </a>
              ) : (
                <a
                  href="https://unsplash.com?utm_source=macaw&utm_medium=referral"
                  target="_blank"
                  rel="noreferrer"
                >
                  Unsplash
                </a>
              )}
            </footer>
          )}
        </div>
      </figure>
      <form onSubmit={handleSubmit(onSubmit)} className={styles.footer}>
        <Label
          label="Alt tag"
          orientation="horizontal"
          className={styles.footerLabel}
        >
          <Controller
            name="alt"
            control={control}
            render={({ field }) => (
              <TextInput
                className={styles.footerInput}
                size="small"
                {...field}
              />
            )}
          />
        </Label>
        <Button
          type="submit"
          variant="primary"
          size="small"
          loading={isSubmitting}
          disabled={!isDirty || isSubmitting}
        >
          Save
        </Button>
      </form>
    </>
  );
}

function HeroPreviewModal({
  isOpen,
  onClose,
  image,
}: {
  isOpen: boolean;
  onClose: () => void;
  image: {
    id: string;
    src: string;
    alt?: string | null;
    height: number;
    width: number;
    provider: ImageProvider;
    author?: {
      name: string;
      href: string;
    };
  };
}) {
  return (
    <Modal
      size="medium"
      isOpen={isOpen}
      onClose={onClose}
      classNames={{ body: styles.heroImagePreviewBody }}
      hideHeader
    >
      <InternalHeroPreviewModal image={image} />
    </Modal>
  );
}

export default HeroPreviewModal;
